// Deliberately the concrete module rather than the `../helpers` barrel. The
// barrel pulls in `menu-access.helpers`, which imports the `../types` barrel
// straight back — so reaching this file through it closes a cycle. That was
// harmless while nothing in `types` imported this file, and stopped being
// harmless when `user.model` did (SD-4591): `UserPhoneUserModel` read as
// `undefined` at construction time depending on which module the graph entered
// first, which fails in a different file on every run.
//
// This breaks the edge that mattered, not the cycle. `user.model` still reaches
// the barrel for `formatDate`, so types → helpers → types is still there; what
// is gone is the second hop that made a class in this file undefined while
// `user.model` was mid-evaluation. Do not "tidy" this back to the barrel.
import { formatPhoneNumber } from "../helpers/formatter.helpers";

// Registration is reported two ways and they disagree on purpose. `is_registered`
// is a live Redis presence read taken per row at request time; `registration_status`
// and `last_registered_at` are the durable projection. A handset that dropped off
// seconds ago reads is_registered=false while the projection still says REGISTERED,
// so the two are shown as separate columns rather than reconciled into one badge.
export enum PHONE_USER_REGISTRATION_STATUS {
  REGISTERED = "REGISTERED",
  UNREGISTERED = "UNREGISTERED",
  NEVER_REGISTERED = "NEVER_REGISTERED",
}

export class PhoneUserEnrolmentModel {
  enrolment_token: string;
  deep_link: string;
  expires_at: string;

  constructor(init: any = {}) {
    this.enrolment_token = "";
    this.deep_link = "";
    this.expires_at = "";
    Object.assign(this, init);
  }

}

// What POST .../enrolments/email answers with. `sent_to` is where it actually
// went, which is not necessarily the agent's own address -- the manager may
// redirect a send without that changing who the agent is.
export class PhoneUserEnrolmentEmailModel {
  sent_to: string;
  sent_at: string;

  constructor(init: any = {}) {
    this.sent_to = "";
    this.sent_at = "";
    Object.assign(this, init);
  }
}

export class PhoneUserDeviceModel {
  uuid: string;
  name: string;
  revoked: boolean;
  created: string;
  last_seen_at: string;

  constructor(init: any = {}) {
    this.uuid = "";
    this.name = "";
    this.revoked = false;
    this.created = "";
    this.last_seen_at = "";
    Object.assign(this, init);
  }
}

// Provisioning lifecycle. DELETE never destroys the row — PhoneUserDevice and
// DeviceEnrolment both cascade off it, and (org, user) is unique, so the record
// is the agent's one slot. Since SD-4227 a teardown also frees the DID back to
// the org's pool, so a torn-down agent reads `number: null`, with
// `released_number` recording what they held.
//
// DISABLED and DELETED are the same teardown and differ only in whether the
// manager still sees the row (SD-4367): the ⋮ menu's *Revoke device* leaves the
// agent listed as "Revoked" so they can be handed a number again, *Delete user*
// takes them off the list. The API excludes DELETED from the unfiltered list,
// so it reaches us only via `?status=deleted`.
export enum PHONE_USER_STATUS {
  ACTIVE = "active",
  DISABLED = "disabled",
  DELETED = "deleted",
}

// Which of the ⋮ menu's two destructive entries a DELETE is (SD-4367). Both run
// the same teardown; the value only decides the status written, and so whether
// the row stays on the manager's list. An unknown value is a 400, never a
// silent revoke.
export enum PHONE_USER_DESTROY_ACTION {
  /** *Revoke device* — the agent stays listed, reading "Revoked". */
  REVOKE = "revoke",
  /** *Delete user* — the row drops off the list. */
  DELETE = "delete",
}

// The states the STATUS column draws. `status` says provisioned-or-torn-down
// and `active_device_name` says enrolled-or-not, but neither can express "an
// invite went out and has not been taken up" — an agent mid-enrolment would
// otherwise read identically to one never invited. That is what
// `has_pending_enrolment` adds (SD-4265, closing SD-4262 scope item 1).
//
// The emailed channel only, under SD-4400. It was the union of that and a live
// enrolment code, which made every agent a manager added read INVITED for the
// code's fifteen minutes with nothing in any inbox — a code is a thing the
// manager is holding, and the server cannot know whether it was ever shown to
// anyone. So an agent holding an unspent code reads NOT_ENROLLED, on purpose:
// SD-4400 scopes that out of the web app deliberately, and the label is correct
// the day the API deploys.
//
// REVOKED is the fourth (SD-4367). Now that revoking leaves the agent on the
// list rather than taking them off it, a torn-down row would otherwise read
// NOT_ENROLLED — indistinguishable from someone added a minute ago and awaiting
// their first invite.
export enum PHONE_USER_ENROLMENT_STATE {
  ENROLLED = "enrolled",
  INVITED = "invited",
  NOT_ENROLLED = "not_enrolled",
  REVOKED = "revoked",
}

export class PhoneUserModel {
  uuid: string;
  // Bare E.164 string, not a UUID — the POST/PATCH contract takes the number
  // itself. Null once the DID has been released back to the org's pool on
  // disable (SD-4227); `released_number` is the record of which one it was.
  number: string | null;
  released_number: string;
  // The member's UUID, not a nested object — identity arrives alongside it as
  // `user_email` / `user_display_name`.
  user: string;
  user_email: string;
  user_display_name: string;
  status: PHONE_USER_STATUS | string;
  is_registered: boolean;
  registration_status: PHONE_USER_REGISTRATION_STATUS | string;
  last_registered_at: string;
  // SD-4265. `null` means no live device; `""` means a device enrolled without
  // a label. The API keeps those apart deliberately (see
  // PhoneUser.active_device_name) — collapsing them here would make "never
  // enrolled" and "enrolled but unnamed" indistinguishable, which is the exact
  // distinction the DEVICE column exists to draw.
  active_device_name: string | null;
  has_pending_enrolment: boolean;
  pending_enrolment_issued_at: string | null;
  // SD-4593. Whether the agent's user is still a member of this org, answered
  // once for the whole page rather than per row. It is not an enrolment fact
  // and deliberately does not feed `enrolment_state()`: a non-member's handset
  // is still enrolled and still registered, it simply gets a 403 from
  // SIPAuthView every time it tries to call. The two are independent, so they
  // stay independent here.
  //
  // Nullable because the three answers are not two: true, false, and "this
  // build is talking to an API that does not send the field yet". Declaring it
  // a plain boolean would invite the direct `row.is_org_member ? ...` read that
  // `has_org_membership` exists to prevent, and under `strict` it is the type
  // that would catch it.
  is_org_member: boolean | null;
  created: string;
  modified: string;

  constructor(init: any = {}) {
    this.uuid = "";
    this.number = null;
    this.released_number = "";
    this.user = "";
    this.user_email = "";
    this.user_display_name = "";
    this.status = PHONE_USER_STATUS.ACTIVE;
    this.is_registered = false;
    this.registration_status = PHONE_USER_REGISTRATION_STATUS.NEVER_REGISTERED;
    this.last_registered_at = "";
    this.active_device_name = null;
    this.has_pending_enrolment = false;
    this.pending_enrolment_issued_at = null;
    // Unknown, not "a member". Defaulting this to `true` would have asserted
    // a membership nobody confirmed and collapsed the three states back into
    // two the moment a payload arrived without the key -- which, until SD-4593
    // deploys, is every payload. `has_org_membership` reads both the same way,
    // so the badge does not change; what changes is that a later caller can
    // still tell "confirmed member" from "never answered".
    this.is_org_member = null;
    this.created = "";
    this.modified = "";
    Object.assign(this, init);
  }

  // Tested for ACTIVE rather than against DISABLED so a status this build does
  // not know about fails closed — the same way every lifecycle gate on the API
  // side does. Written the other way round, SD-4367's `deleted` would have read
  // as active and let a torn-down agent hold a DID in the picker.
  is_active() {
    return this.status === PHONE_USER_STATUS.ACTIVE;
  }

  pretty_number() {
    return this.number ? formatPhoneNumber(this.number) : "";
  }

  display_name() {
    return this.user_display_name || this.user_email || "—";
  }

  has_enrolled() {
    return this.active_device_name !== null;
  }

  // Tested against an explicit `false` rather than for falsiness, which is the
  // opposite of `is_active`'s fail-closed reading and deliberately so: there,
  // an unknown status must not grant a capability; here, an unknown membership
  // must not level an accusation. The field lands with SD-4593 and this build
  // may well reach production first, so every row would arrive without the key
  // and read as expelled -- the whole list wearing a red badge. `null` is the
  // API declining to answer, not a denial, and only a denial draws the badge.
  has_org_membership() {
    return this.is_org_member !== false;
  }

  // Empty string when there is no device, so callers render their own dash the
  // way `pretty_number` lets them. An unnamed device is still a device and must
  // not read as an absent one.
  device_label() {
    if (this.active_device_name === null) return "";
    return this.active_device_name || "Unnamed device";
  }

  // An enrolled agent who has just been re-invited stays ENROLLED: their live
  // device keeps working until the new handset completes its exchange
  // (PhoneUserDevice.enrol revokes the previous device at enrolment, not at
  // issue), so reporting them as merely invited would understate what they have.
  //
  // A torn-down agent is REVOKED before anything else, and the precedence costs
  // nothing: the teardown revokes every device, drops outstanding codes and
  // clears the emailed invite, so the other two branches cannot be true of a
  // row that is not active. It exists to stop such a row falling through to
  // NOT_ENROLLED.
  enrolment_state() {
    if (!this.is_active()) return PHONE_USER_ENROLMENT_STATE.REVOKED;
    if (this.has_enrolled()) return PHONE_USER_ENROLMENT_STATE.ENROLLED;
    if (this.has_pending_enrolment) return PHONE_USER_ENROLMENT_STATE.INVITED;
    return PHONE_USER_ENROLMENT_STATE.NOT_ENROLLED;
  }
}

// The fourth value `phone_user.status` can take, and the only one with no
// PHONE_USER_STATUS member (SD-4590). It means the user has never held an agent
// slot on this org -- not that a row exists in some neutral state. Kept apart
// from the enum on purpose: PHONE_USER_STATUS is the status of a row, and there
// is no row here.
export const PHONE_USER_ORG_STATUS_NONE = "none";

// The four values the field can actually take. Spelled out rather than the
// `PHONE_USER_STATUS | string` the neighbouring classes use: that union widens
// to `string`, so the compiler never checks a comparison against it and no
// reader can enumerate the legal values from the type.
export type UserPhoneUserStatus =
  | PHONE_USER_STATUS
  | typeof PHONE_USER_ORG_STATUS_NONE;

// A member's agent slot on one org, as GET /admin/org/<org>/user[/<uuid>]
// reports it since SD-4590: `{status, did}` and nothing else. In particular
// there is no phone-user uuid, so a caller wanting to link to the agent's row
// has to resolve it separately.
//
// `phoneuser_unique_org_user` makes it at most one row, which is why this is a
// single object rather than a list.
export class UserPhoneUserModel {
  status: UserPhoneUserStatus;
  // Null for a non-agent and for a deleted agent. For a Revoked agent it is the
  // released snapshot rather than a live assignment (SD-4227) -- which is the
  // whole point, since a Revoked agent is one whose removal is refused by name.
  did: string | null;

  constructor(init: any = {}) {
    this.status = PHONE_USER_ORG_STATUS_NONE;
    this.did = null;
    // `|| {}` unlike the four classes above, which take `Object.assign(this,
    // init)` and would throw here. This is the only one handed a value straight
    // off a payload that may be `null` -- `phone_user: null` is not a shape the
    // API sends today, but the default argument only covers `undefined`.
    Object.assign(this, init || {});
  }

  // The two states SD-4583 refuses to remove from the org. Tested for the
  // blocking values rather than against the passing ones so a status this build
  // does not know about falls through to the passcode box and the API's own
  // refusal (SD-4592), rather than blocking a delete that is actually allowed.
  // The same reasoning as PhoneUserModel.is_active, pointed the other way:
  // there the unknown value must not grant, here it must not deny.
  blocks_user_delete() {
    return (
      this.status === PHONE_USER_STATUS.ACTIVE ||
      this.status === PHONE_USER_STATUS.DISABLED
    );
  }

  // Empty when the API reports no DID, so the caller can drop the "on <DID>"
  // clause rather than print "an agent on .". Reachable for a Revoked agent
  // whose released snapshot was never written.
  pretty_did() {
    return this.did ? formatPhoneNumber(this.did) : "";
  }
}
